
import * as React from "react"
import { cn } from "@/lib/utils"

const SidebarContext = React.createContext<{
  isExpanded: boolean
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>
}>({
  isExpanded: true,
  setIsExpanded: () => undefined,
})

export function SidebarProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [isExpanded, setIsExpanded] = React.useState(true)

  return (
    <SidebarContext.Provider value={{ isExpanded, setIsExpanded }}>
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebar() {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider")
  }
  return context
}

export function Sidebar({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  const { isExpanded } = useSidebar()

  return (
    <aside
      className={cn(
        "h-screen sticky top-0 transition-all duration-300 ease-in-out border-r bg-[#1A1F2C] backdrop-blur supports-[backdrop-filter]:bg-background/60",
        isExpanded ? "w-64" : "w-16",
        className
      )}
    >
      {children}
    </aside>
  )
}

export function SidebarTrigger({
  className,
}: {
  className?: string
}) {
  const { isExpanded, setIsExpanded } = useSidebar()

  return (
    <button
      onClick={() => setIsExpanded(!isExpanded)}
      className={cn(
        "fixed top-4 left-4 z-50 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-all duration-300 ease-in-out hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
        isExpanded && "left-60",
        className
      )}
    >
      {isExpanded ? "→" : "←"}
    </button>
  )
}

export function SidebarContent({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return <div className={cn("flex flex-col h-full", className)}>{children}</div>
}

export function SidebarHeader({
  className,
  children,
}: {
  className?: string
  children?: React.ReactNode
}) {
  const { isExpanded } = useSidebar()

  return (
    <div
      className={cn(
        "flex h-14 items-center border-b border-border px-4",
        isExpanded ? "justify-between" : "justify-center",
        className
      )}
    >
      {children}
    </div>
  )
}

export function SidebarFooter({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        "flex items-center border-t border-border p-4",
        className
      )}
    >
      {children}
    </div>
  )
}

export function SidebarGroup({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return <div className={cn("py-2", className)}>{children}</div>
}

export function SidebarGroupLabel({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  const { isExpanded } = useSidebar()

  if (!isExpanded) {
    return null
  }

  return (
    <div
      className={cn(
        "px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground",
        className
      )}
    >
      {children}
    </div>
  )
}

export function SidebarGroupContent({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return <div className={cn("", className)}>{children}</div>
}

export function SidebarMenu({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return <nav className={cn("grid gap-1", className)}>{children}</nav>
}

export function SidebarMenuItem({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return <div className={cn("", className)}>{children}</div>
}

export function SidebarMenuButton({
  className,
  asChild,
  children,
  ...props
}: {
  className?: string
  asChild?: boolean
  children: React.ReactNode
  [key: string]: any
}) {
  const { isExpanded } = useSidebar()
  const Comp = asChild ? React.Fragment : "button"
  const childProps = asChild ? {} : props

  if (asChild) {
    return React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return null

      return React.cloneElement(child as React.ReactElement, {
        className: cn(
          "flex w-full cursor-pointer items-center gap-3 rounded-md px-4 py-2 text-sm text-foreground transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring",
          !isExpanded && "justify-center px-0",
          child.props.className,
          className
        ),
      })
    })
  }

  return (
    <Comp
      className={cn(
        "flex w-full cursor-pointer items-center gap-3 rounded-md px-4 py-2 text-sm text-foreground transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring",
        !isExpanded && "justify-center px-0",
        className
      )}
      {...childProps}
    >
      {children}
    </Comp>
  )
}
